import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import * as O from "@/components/Ornaments";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  GuestbookSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════
// 1. EDITORIAL — Asal · classic editorial wedding (heirloom magazine)
// ═══════════════════════════════════════════════════════════════════════
//
// The Asal template is meant to feel like a heirloom — something you'd
// frame and hang. References: Vogue/Vanity Fair masthead rhythm (issue
// numbers, hairline rules, oversized italic mastheads), Paperless Post's
// wax-seal motif, Minted's deckle-edge cards, Withjoy's editorial save-
// the-dates with refined long-form dates ("the twelfth of June").
//
// Signature element unique to Asal:
//   A pressed WAX SEAL with the couple's monogram (A·S) sits next to the
//   date on the hero. It's an SVG with a radial gradient that mimics the
//   gloss of cooled wax. On hover, it rotates a few degrees and emits a
//   soft "ding" — like lifting and re-pressing a stamp.
//
// Other refinements:
//   - Magazine-style masthead row with VOL. / NO. / EST.
//   - Drop cap on the story passage; oversized italic ampersand pull-quote
//     (Navoiy-attributed) between gallery and bridal-party
//   - Scroll reveals (IntersectionObserver) gated on prefers-reduced-motion
//   - Photo treatment: sepia ease, gold hairline frame, vignette, parallax
//   - Sounds: chime on RSVP submit, click on primary buttons, music toggle,
//     swoosh on hero entry, ding on wax-seal hover
//   - Typography: lining numerals, ligatures, ss01 stylistic set hint
// ═══════════════════════════════════════════════════════════════════════

// ─── Reveal-on-scroll wrapper ──────────────────────────────────────────
// Generic IntersectionObserver-based fade/slide-in. Respects
// prefers-reduced-motion (skips animation entirely if reduced).
function Reveal({
  children,
  delay = 0,
  y = 24,
  reduceMotion,
  onShow,
  style,
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  reduceMotion: boolean;
  onShow?: () => void;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (reduceMotion) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            onShow?.();
            io.disconnect();
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduceMotion, onShow]);

  const transition = reduceMotion
    ? "none"
    : `opacity .9s cubic-bezier(.22,.61,.36,1) ${delay}ms, transform .9s cubic-bezier(.22,.61,.36,1) ${delay}ms`;

  return (
    <div
      ref={ref}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : `translateY(${y}px)`,
        transition,
        willChange: visible ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── Wax-seal SVG (signature element) ──────────────────────────────────
function WaxSeal({
  initials,
  font,
  accent,
  size = 88,
  reduceMotion,
  onHover,
}: {
  initials: string;
  font: string;
  accent: string;
  size?: number;
  reduceMotion: boolean;
  onHover?: () => void;
}) {
  const [hover, setHover] = useState(false);
  const rot = hover && !reduceMotion ? -6 : -3;
  return (
    <button
      type="button"
      onPointerEnter={() => {
        setHover(true);
        onHover?.();
      }}
      onFocus={() => {
        setHover(true);
        onHover?.();
      }}
      onMouseLeave={() => setHover(false)}
      onBlur={() => setHover(false)}
      style={{
        width: size,
        height: size,
        transform: `rotate(${rot}deg) ${hover && !reduceMotion ? "scale(1.04)" : "scale(1)"}`,
        transition: reduceMotion ? "none" : "transform .35s cubic-bezier(.22,.61,.36,1)",
        cursor: "default",
        filter: `drop-shadow(0 4px 8px ${accent}55)`,
        background: "transparent",
        border: 0,
        padding: 0,
      }}
      aria-label="Heirloom monogram"
    >
      <svg viewBox="0 0 100 100" width={size} height={size}>
        <defs>
          <radialGradient id="wax-grad" cx="32%" cy="28%" r="78%">
            <stop offset="0%" stopColor="#9a3a47" />
            <stop offset="40%" stopColor={accent} />
            <stop offset="100%" stopColor="#3a0d14" />
          </radialGradient>
          <radialGradient id="wax-gloss" cx="30%" cy="22%" r="35%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          <filter id="wax-rough" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence baseFrequency="0.9" numOctaves="2" seed="3" />
            <feDisplacementMap in="SourceGraphic" scale="1.6" />
          </filter>
        </defs>
        {/* irregular wax blob — slightly star-like, deckle edge */}
        <path
          d="M50 4 L60 12 L72 9 L75 21 L86 26 L82 38 L92 48 L84 58 L90 70 L78 73 L76 86 L63 84 L54 94 L44 88 L31 92 L28 80 L15 78 L18 65 L8 56 L16 46 L9 34 L21 30 L20 17 L33 18 Z"
          fill="url(#wax-grad)"
          filter="url(#wax-rough)"
        />
        {/* gloss highlight */}
        <ellipse cx="38" cy="32" rx="22" ry="14" fill="url(#wax-gloss)" />
        {/* outer impressed ring */}
        <circle cx="50" cy="50" r="32" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.6" />
        <circle cx="50" cy="50" r="28" fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth="0.5" />
        {/* monogram */}
        <text
          x="50"
          y="58"
          textAnchor="middle"
          fontFamily={font}
          fontStyle="italic"
          fontSize="26"
          fontWeight="500"
          fill="rgba(255,236,210,0.95)"
          style={{ letterSpacing: "-0.02em" }}
        >
          {initials}
        </text>
        {/* tiny stars flanking initials */}
        <g fill="rgba(255,236,210,0.7)">
          <circle cx="28" cy="50" r="0.9" />
          <circle cx="72" cy="50" r="0.9" />
        </g>
      </svg>
    </button>
  );
}

// ─── Floating sound toggle (top-right) ─────────────────────────────────
function SoundToggle({
  enabled,
  onToggle,
  accent,
  ink,
  bg,
}: {
  enabled: boolean;
  onToggle: () => void;
  accent: string;
  ink: string;
  bg: string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onToggle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={enabled ? "Ovozni o'chirish" : "Ovozni yoqish"}
      style={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 50,
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: bg,
        border: `1px solid ${accent}`,
        color: ink,
        cursor: "pointer",
        boxShadow: hover ? `4px 5px 0 -1px ${accent}` : `3px 4px 0 -1px ${accent}88`,
        transform: hover ? "translate(-1px,-1px)" : "translate(0,0)",
        transition: "transform .2s ease, box-shadow .2s ease",
        display: "grid",
        placeItems: "center",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="1.6">
        {enabled ? (
          <>
            <path d="M3 10v4h4l5 4V6L7 10H3z" fill={accent} />
            <path d="M16 8c1.5 1 1.5 7 0 8" />
            <path d="M19 5c3 2 3 12 0 14" />
          </>
        ) : (
          <>
            <path d="M3 10v4h4l5 4V6L7 10H3z" fill={accent} />
            <line x1="16" y1="8" x2="22" y2="14" />
            <line x1="22" y1="8" x2="16" y2="14" />
          </>
        )}
      </svg>
    </button>
  );
}

// ─── Main variant ──────────────────────────────────────────────────────
export function VariantEditorial({ t, isDark, ctx }: VariantProps) {
  const { play, enabled: soundEnabled, toggle: toggleSound } = useSound();
  const tt = useT();
  const p = t.photos || [];

  // Reduced-motion query (kept reactive in case the user toggles).
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Hero photo subtle parallax — translateY based on scrollY.
  const heroPhotoRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (reduceMotion) return;
    const el = heroPhotoRef.current;
    if (!el) return;
    let ticking = false;
    const update = () => {
      const y = window.scrollY || 0;
      el.style.transform = `translateY(${Math.min(80, y * 0.12)}px) scale(1.05)`;
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [reduceMotion]);

  // Track whether the user has already submitted RSVP so we only chime once.
  const rsvpChimedRef = useRef<boolean>(false);
  const heroRevealedRef = useRef<boolean>(false);

  // Names parsing.
  const [first, second] = useMemo(() => {
    const parts = t.names.split("&").map((s) => s.trim());
    return [parts[0] || t.names, parts[1] || ""];
  }, [t.names]);

  // Long-form date in Uzbek style for the editorial nameplate row.
  const dParts = t.date.split(".");
  const monthNames = ["Yanvar","Fevral","Mart","Aprel","May","Iyun","Iyul","Avgust","Sentyabr","Oktyabr","Noyabr","Dekabr"];
  const longDate = dParts.length === 3
    ? `${dParts[0]} · ${monthNames[Math.max(0, Math.min(11, parseInt(dParts[1], 10) - 1))]} · ${dParts[2]}`
    : t.date;
  const roman = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII"];
  const issueNo = dParts.length === 3
    ? `NO. ${dParts[0]} · ${roman[Math.max(0, Math.min(11, parseInt(dParts[1], 10) - 1))]} · ${dParts[2]}`
    : `NO. ${t.date}`;

  // Watch RSVP state — chime once when transitioning to "yes".
  useEffect(() => {
    if (ctx.rsvp.rsvp === "yes" && !rsvpChimedRef.current) {
      rsvpChimedRef.current = true;
      play("chime");
    }
    if (ctx.rsvp.rsvp === null) rsvpChimedRef.current = false;
  }, [ctx.rsvp.rsvp, play]);

  const accent = t.accent;
  const ink = t.ink;
  const bg = t.bg;

  // Subtle paper-texture backdrop layered behind the page.
  const paperBg: CSSProperties = {
    background: `
      radial-gradient(ellipse at 20% 0%, ${accent}0a, transparent 50%),
      radial-gradient(ellipse at 80% 100%, ${accent}08, transparent 55%),
      ${bg}
    `,
  };

  return (
    <div
      className="asal-variant"
      style={{
        ...paperBg,
        fontFeatureSettings: "'lnum','dlig','liga','kern'",
      }}
    >
      {/* Inline keyframes & helpers — scoped via unique class names */}
      <style>{`
        @keyframes asal-rule-draw {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes asal-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .asal-rule {
          display: inline-block;
          height: 1px;
          background: currentColor;
          transform-origin: left center;
          animation: asal-rule-draw 1.4s cubic-bezier(.22,.61,.36,1) .3s both;
        }
        .asal-name-link {
          display: inline-block;
          position: relative;
        }
        .asal-name-link::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: -6px;
          height: 1px;
          background: currentColor;
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform .55s cubic-bezier(.22,.61,.36,1);
        }
        .asal-name-link:hover::after {
          transform: scaleX(1);
        }
        .asal-photo-frame {
          position: relative;
          overflow: hidden;
        }
        .asal-photo-frame::before {
          content: "";
          position: absolute;
          inset: 10px;
          border: 1px solid rgba(255,255,255,0.32);
          pointer-events: none;
          z-index: 2;
        }
        .asal-photo-frame::after {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.35) 100%);
          pointer-events: none;
          z-index: 1;
        }
        .asal-photo-zoom {
          transition: transform 1.2s cubic-bezier(.22,.61,.36,1), filter .8s ease;
        }
        .asal-photo-frame:hover .asal-photo-zoom {
          transform: scale(1.06);
          filter: sepia(0.05) saturate(1.05);
        }
        .asal-dropcap::first-letter {
          font-family: var(--font-display, serif);
          font-style: italic;
          font-weight: 500;
          font-size: 4.2em;
          float: left;
          line-height: 0.9;
          padding: 4px 12px 0 0;
          color: var(--asal-accent, currentColor);
        }
        .asal-btn {
          position: relative;
          overflow: hidden;
          transition: transform .2s ease, box-shadow .2s ease;
        }
        .asal-btn:hover {
          transform: translate(-1px,-1px);
        }
        .asal-corner {
          transition: transform .6s cubic-bezier(.22,.61,.36,1);
        }
        .asal-corner:hover {
          transform: rotate(8deg);
        }
        @media (prefers-reduced-motion: reduce) {
          .asal-rule, .asal-name-link::after, .asal-photo-zoom, .asal-btn, .asal-corner {
            animation: none !important;
            transition: none !important;
          }
          .asal-name-link:hover::after { transform: scaleX(0); }
          .asal-photo-frame:hover .asal-photo-zoom { transform: none; filter: none; }
        }

        /* ─────────── MOBILE — phone-tailored editorial layout ─────────── */
        @media (max-width: 768px) {
          /* Hero: photo above, names below — flip the source order */
          .asal-variant .asal-hero {
            min-height: auto !important;
            grid-template-columns: 1fr !important;
          }
          .asal-variant .asal-hero-text {
            padding: 64px 18px 48px !important;
            border-right: none !important;
            order: 2;
            text-align: center;
          }
          .asal-variant .asal-hero-photo {
            order: 1;
            aspect-ratio: auto;
            width: 100%;
            height: min(60vh, 560px);
            border-radius: 0;
            overflow: hidden;
            margin: 0;
            box-shadow: 0 8px 24px rgba(0,0,0,0.18);
            position: relative;
          }
          /* Magazine-page photo treatment: wine inner stroke */
          .asal-variant .asal-hero-photo::after {
            content: "";
            position: absolute;
            inset: 8px;
            border: 1px solid var(--asal-accent, currentColor);
            border-radius: calc(8% - 2px);
            pointer-events: none;
            z-index: 3;
            opacity: 0.85;
          }

          /* Compact masthead */
          .asal-variant .asal-masthead {
            font-size: 8px !important;
            letter-spacing: 0.25em !important;
            margin-bottom: 18px !important;
            padding-bottom: 10px !important;
            gap: 8px;
          }
          .asal-variant .asal-eyebrow {
            font-size: 9px !important;
            letter-spacing: 0.4em !important;
            margin-bottom: 20px !important;
          }

          /* Larger, intentional names */
          .asal-variant .asal-names {
            font-size: clamp(36px, 12vw, 72px) !important;
            line-height: 0.95 !important;
            margin-bottom: 18px !important;
          }
          .asal-variant .asal-seal-row {
            justify-content: center;
            align-items: center !important;
          }
          .asal-variant .asal-story {
            padding: 64px 18px !important;
          }
          .asal-variant .asal-pullquote {
            padding: 64px 18px !important;
          }
          .asal-variant .asal-bridal-party {
            padding: 56px 18px !important;
          }

          /* Caption card: smaller, tucked tighter */
          .asal-variant .asal-caption-card {
            bottom: 18px !important;
            right: 18px !important;
            padding: 12px 16px !important;
            font-size: 9px !important;
            letter-spacing: 0.22em !important;
          }
          .asal-variant .asal-caption-card > div:nth-child(2) {
            font-size: 20px !important;
          }

          /* Wax seal: tucked into bottom-right corner of hero photo */
          .asal-variant .asal-hero-photo .asal-wax-seal-corner {
            display: block;
          }
          .asal-variant .asal-seal-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px !important;
          }
          .asal-variant .asal-wax-seal-wrap {
            transform: scale(0.7);
            transform-origin: left top;
          }
          .asal-variant .asal-love-tag {
            display: block !important;
          }

          /* Story: smaller drop cap, tighter padding, body 16/1.6 */
          .asal-variant .asal-story {
            padding: 64px 24px !important;
          }
          .asal-variant .asal-story-body {
            font-size: 16px !important;
            line-height: 1.6 !important;
          }
          .asal-variant .asal-dropcap::first-letter {
            font-size: 3.4em !important;
            padding: 2px 10px 0 0 !important;
          }

          /* Pull-quote: cap ampersand size, generous breathing room */
          .asal-variant .asal-pullquote {
            padding: 64px 24px !important;
          }
          .asal-variant .asal-amp {
            font-size: clamp(80px, 28vw, 110px) !important;
            margin-bottom: -12px !important;
          }
          .asal-variant .asal-quote {
            font-size: 19px !important;
            line-height: 1.5 !important;
            padding: 8px 4px !important;
          }

          /* Bridal party: hide diptych photo, names focus */
          .asal-variant .asal-diptych {
            grid-template-columns: 1fr !important;
          }
          .asal-variant .asal-diptych-photo {
            aspect-ratio: 16 / 9 !important;
          }
          .asal-variant .asal-bridal-party {
            aspect-ratio: auto !important;
            padding: 56px 24px !important;
          }
          .asal-variant .asal-bridal-title {
            font-size: 38px !important;
            margin-bottom: 24px !important;
          }
          .asal-variant .asal-bridal-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          .asal-variant .asal-bridal-list {
            font-size: 17px !important;
            line-height: 1.7 !important;
          }
        }
      `}</style>

      <SoundToggle
        enabled={soundEnabled}
        onToggle={() => {
          play(soundEnabled ? "music-off" : "music-on");
          toggleSound();
        }}
        accent={accent}
        ink={ink}
        bg={bg}
      />

      {/* ─────────── HERO — editorial split with masthead ─────────── */}
      <section
        className="asal-hero"
        style={{
          minHeight: "100vh",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0,
          position: "relative",
        }}
      >
        {/* LEFT: typographic plate */}
        <div
          className="asal-hero-text"
          style={{
            padding: "64px 56px 80px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            borderRight: `1px solid ${ink}22`,
            position: "relative",
            // @ts-expect-error CSS var
            "--asal-accent": accent,
          }}
        >
          {/* Masthead row */}
          <div
            className="asal-masthead"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontFamily: "var(--font-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: accent,
              marginBottom: 28,
              borderBottom: `1px solid ${ink}33`,
              paddingBottom: 14,
              fontFeatureSettings: "'lnum','tnum'",
            }}
          >
            <span>VOL. I</span>
            <span style={{ color: `${ink}99` }}>{issueNo}</span>
            <span>EST. {dParts[2] || ""}</span>
          </div>

          <div
            className="asal-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: accent,
              marginBottom: 32,
            }}
          >
            {tt("variant.w01.hero.eyebrow")}
          </div>

          {/* Names — stacked italic, asymmetric, hover underline */}
          <h1
            className="asal-names"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(56px, 9vw, 118px)",
              fontStyle: "italic",
              fontWeight: 400,
              lineHeight: 0.9,
              letterSpacing: "-0.025em",
              marginBottom: 24,
              fontFeatureSettings: "'dlig','liga','kern'",
            }}
          >
            <span className="asal-name-link">{first}</span>
            <br />
            <span
              style={{
                color: accent,
                fontFamily: "var(--font-hand)",
                fontStyle: "normal",
                fontSize: "0.46em",
                display: "inline-block",
                margin: "0.1em 0 0.05em",
                letterSpacing: 0,
              }}
            >
              {tt("variant.w01.hero.and")}
            </span>
            <br />
            <span style={{ marginLeft: "0.25em" }} className="asal-name-link">
              {second || t.names}
            </span>
          </h1>

          {/* Drawn-in hairline + flourish */}
          <div style={{ color: accent, marginBottom: 28, display: "flex", alignItems: "center", gap: 12 }}>
            <span className="asal-rule" style={{ width: 80, color: accent }} />
            <O.Flourish width={120} color={accent} />
            <span className="asal-rule" style={{ width: 80, color: accent }} />
          </div>

          {/* Long-form date */}
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.32em",
              color: `${ink}cc`,
              marginBottom: 24,
              fontFeatureSettings: "'lnum','tnum'",
            }}
          >
            {longDate.toUpperCase()}
          </div>

          <p
            style={{
              fontFamily: "var(--font-body)",
              fontStyle: "italic",
              fontSize: 19,
              lineHeight: 1.65,
              maxWidth: 460,
              color: `${ink}cc`,
              marginBottom: 36,
            }}
          >
            {tt("variant.w01.hero.body")}
          </p>

          {/* Wax seal + secondary photo row */}
          <div className="asal-seal-row" style={{ display: "flex", alignItems: "flex-end", gap: 28 }}>
            <div className="asal-wax-seal-wrap">
            <WaxSeal
              initials={`${first[0] || "A"}·${(second[0] || "S")}`}
              font={t.font}
              accent={accent}
              size={92}
              reduceMotion={reduceMotion}
              onHover={() => play("ding")}
            />
            <div
              className="mobile-only asal-love-tag"
              style={{
                display: "none",
                fontFamily: "var(--font-hand)",
                fontSize: 24,
                color: accent,
                marginTop: 8,
                lineHeight: 1,
              }}
            >
              {tt("variant.w01.hero.love_tag")}
            </div>
            </div>
            {p[1] && (
              <div
                className="asal-photo-frame"
                style={{
                  width: "55%",
                  aspectRatio: "4/5",
                  flex: "0 0 auto",
                  boxShadow: `8px 10px 0 -1px ${accent}33`,
                }}
              >
                <GuestPhoto
                  src={p[1]}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallbackBg={bg}
                  fallbackTone={accent}
                />
                <div className="asal-photo-zoom" style={{ position: "absolute", inset: 0 }}>
                  <GuestPhoto
                    src={p[1]}
                    style={{ width: "100%", height: "100%", filter: "sepia(0.12) saturate(0.95)" }}
                    fallbackBg={bg}
                    fallbackTone={accent}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: bleed photo with gold-frame caption card */}
        <div className="asal-hero-photo" style={{ position: "relative", overflow: "hidden" }}>
          <div
            ref={heroPhotoRef}
            style={{
              position: "absolute",
              inset: "-3%",
              willChange: "transform",
              transform: "scale(1.05)",
            }}
          >
            <GuestPhoto
              src={p[0]}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                filter: "sepia(0.14) saturate(0.92) contrast(1.02)",
              }}
              fallbackBg={bg}
              fallbackTone={accent}
            />
          </div>
          {/* Vignette overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.45) 100%), linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.55) 100%)",
              pointerEvents: "none",
            }}
          />
          {/* Inset gold hairline frame */}
          <div
            style={{
              position: "absolute",
              inset: 24,
              border: `1px solid rgba(255,255,255,0.3)`,
              boxShadow: "inset 0 0 0 4px rgba(0,0,0,0.06)",
              pointerEvents: "none",
            }}
          />
          {/* Caption card */}
          <Reveal reduceMotion={reduceMotion} delay={400} y={20} onShow={() => {
            if (!heroRevealedRef.current) {
              heroRevealedRef.current = true;
              play("swoosh");
            }
          }}>
            <div
              className="asal-caption-card"
              style={{
                position: "absolute",
                bottom: 36,
                right: 36,
                padding: "20px 28px",
                background: bg,
                border: `1px solid ${accent}`,
                boxShadow: `6px 7px 0 -1px ${accent}66`,
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.3em",
                color: ink,
              }}
            >
              <div style={{ color: accent, marginBottom: 6 }}>{t.venue.toUpperCase()}</div>
              <div
                style={{
                  fontFamily: t.font,
                  fontStyle: "italic",
                  fontSize: 28,
                  marginTop: 4,
                  letterSpacing: "-0.02em",
                  fontWeight: 400,
                }}
              >
                17:00
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 9,
                  letterSpacing: "0.4em",
                  color: `${ink}99`,
                  fontFeatureSettings: "'lnum'",
                }}
              >
                {tt("variant.w01.hero.caption_label")}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Reveal reduceMotion={reduceMotion}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* Story with drop cap */}
      <Reveal reduceMotion={reduceMotion}>
        <section className="asal-story" style={{ padding: "120px 48px", borderTop: `1px solid ${ink}22`, textAlign: "center" }}>
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: "var(--font-hand)",
                fontSize: 32,
                color: accent,
                marginBottom: 18,
              }}
            >
              {tt("variant.w01.story.eyebrow")}
            </div>
            <h2
              style={{
                fontFamily: t.font,
                fontSize: "clamp(36px, 5vw, 56px)",
                fontStyle: "italic",
                fontWeight: 400,
                marginBottom: 16,
                letterSpacing: "-0.015em",
              }}
            >
              {tt("variant.w01.story.title")}
            </h2>
            <div style={{ color: accent, margin: "12px auto 32px", display: "flex", justifyContent: "center" }}>
              <O.Flourish width={120} color={accent} />
            </div>
            <p
              className="asal-dropcap asal-story-body"
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 19,
                lineHeight: 1.75,
                fontStyle: "italic",
                color: `${ink}cc`,
                textAlign: "left",
                // CSS var for ::first-letter color
                ...({ "--asal-accent": accent } as CSSProperties),
              }}
            >
              {tt("variant.w01.story.body")}
            </p>
          </div>
        </section>
      </Reveal>

      {/* Pull-quote with oversized italic ampersand — Navoiy */}
      <Reveal reduceMotion={reduceMotion}>
        <section
          className="asal-pullquote"
          style={{
            padding: "100px 48px",
            borderTop: `1px solid ${ink}22`,
            background: `${accent}08`,
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Faint ornamental backdrop */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              opacity: 0.06,
              color: accent,
              pointerEvents: "none",
            }}
          >
            <O.Suzani size={420} color={accent} />
          </div>
          <div style={{ maxWidth: 760, margin: "0 auto", position: "relative" }}>
            <div
              className="asal-amp"
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(120px, 18vw, 200px)",
                lineHeight: 0.7,
                color: accent,
                opacity: 0.4,
                marginBottom: -20,
                fontWeight: 400,
              }}
              aria-hidden
            >
              &amp;
            </div>
            <p
              className="asal-quote"
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(22px, 3vw, 32px)",
                fontWeight: 400,
                lineHeight: 1.5,
                color: ink,
                letterSpacing: "-0.005em",
                marginBottom: 24,
              }}
            >
              {tt("variant.w01.quote.text")}
            </p>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.4em",
                color: accent,
              }}
            >
              {tt("variant.w01.quote.author")}
            </div>
          </div>
        </section>
      </Reveal>

      {/* Diptych photo break — uses photos[2] + photos[3] */}
      {(p[2] || p[3]) && (
        <Reveal reduceMotion={reduceMotion}>
          <section className="asal-diptych" style={{ padding: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
            <div className="asal-photo-frame asal-diptych-photo" style={{ aspectRatio: "4/5", position: "relative" }}>
              <div className="asal-photo-zoom" style={{ position: "absolute", inset: 0 }}>
                <GuestPhoto
                  src={p[2] || p[0]}
                  style={{
                    width: "100%",
                    height: "100%",
                    filter: "sepia(0.12) saturate(0.92)",
                  }}
                  fallbackBg={bg}
                  fallbackTone={accent}
                />
              </div>
            </div>
            <div
              className="asal-bridal-party"
              style={{
                aspectRatio: "4/5",
                background: `${accent}10`,
                padding: "60px 48px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {/* deco corner top-left */}
              <div className="asal-corner" style={{ position: "absolute", top: 16, left: 16, color: accent }}>
                <O.DecoCorner size={48} color={accent} />
              </div>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: accent,
                  marginBottom: 24,
                }}
              >
                {tt("variant.w01.bridal.eyebrow")}
              </div>
              <h2
                className="asal-bridal-title"
                style={{
                  fontFamily: t.font,
                  fontSize: "clamp(36px, 5vw, 56px)",
                  fontStyle: "italic",
                  fontWeight: 400,
                  marginBottom: 32,
                  lineHeight: 1.05,
                  letterSpacing: "-0.015em",
                }}
              >
                {tt("variant.w01.bridal.title")}
              </h2>
              <div className="asal-bridal-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                {([
                  [tt("variant.w01.bridal.groom_label"), tt("variant.w01.bridal.groom_list")],
                  [tt("variant.w01.bridal.bride_label"), tt("variant.w01.bridal.bride_list")],
                ] as const).map(([k, v]) => (
                  <div key={k}>
                    <div
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.3em",
                        color: accent,
                        marginBottom: 8,
                      }}
                    >
                      {k.toUpperCase()}
                    </div>
                    <div
                      className="asal-bridal-list"
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: 15,
                        fontStyle: "italic",
                        lineHeight: 1.6,
                        color: `${ink}cc`,
                      }}
                    >
                      {v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>
      )}

      <Reveal reduceMotion={reduceMotion}>
        <GallerySection t={t} />
      </Reveal>
      <Reveal reduceMotion={reduceMotion}>
        <ScheduleSection
          t={t}
          items={[
            [tt("variant.w01.schedule.0.time"), tt("variant.w01.schedule.0.event")],
            [tt("variant.w01.schedule.1.time"), tt("variant.w01.schedule.1.event")],
            [tt("variant.w01.schedule.2.time"), tt("variant.w01.schedule.2.event")],
            [tt("variant.w01.schedule.3.time"), tt("variant.w01.schedule.3.event")],
            [tt("variant.w01.schedule.4.time"), tt("variant.w01.schedule.4.event")],
          ]}
        />
      </Reveal>
      <Reveal reduceMotion={reduceMotion}>
        <MapSection t={t} isDark={isDark} addressLine={`${t.venue} · ${tt("variant.w01.map.address")}`} />
      </Reveal>

      {/* RSVP — wraps the section so we can intercept clicks for sound */}
      <Reveal reduceMotion={reduceMotion}>
        <div
          onClickCapture={(e) => {
            const tgt = e.target as HTMLElement;
            if (tgt && tgt.closest("button")) play("click");
          }}
        >
          <RsvpSection t={t} isDark={isDark} {...ctx.rsvp} />
        </div>
      </Reveal>

      <Reveal reduceMotion={reduceMotion}>
        <div
          onClickCapture={(e) => {
            const tgt = e.target as HTMLElement;
            if (tgt && tgt.closest("button")) play("tap");
          }}
        >
          <GuestbookSection t={t} isDark={isDark} {...ctx.guestbook} />
        </div>
      </Reveal>

      <Reveal reduceMotion={reduceMotion}>
        <div
          onClickCapture={(e) => {
            const tgt = e.target as HTMLElement;
            if (tgt && tgt.closest("button")) play("pop");
          }}
        >
          <ShareSection t={t} />
        </div>
      </Reveal>

      <Reveal reduceMotion={reduceMotion}>
        <GuestFooter t={t} />
      </Reveal>
    </div>
  );
}
